import {PayloadAction, createSelector, createSlice} from '@reduxjs/toolkit';

import {MoodLight, getHeatProfileMoodLight} from '../../types';
import {isDefined} from '../../utils/types';
import {MoodLightStore, Store} from '../types';
import {
  currentDeviceHeatProfilesSelector,
  currentDeviceModelNumberSelector,
} from './bleSlice';

type Except = {except: string[]};
type Only = {only: string[]};

const isExcept = (o: Except | Only | Record<string, any>): o is Except =>
  !!(o as Except).except;
const isOnly = (o: Except | Only | Record<string, any>): o is Only =>
  !!(o as Only).only;

const moodLightSlice = createSlice({
  name: 'moodLight',
  initialState: {
    accountMoodLights: {
      byId: {},
    },
    exclusiveMoodLights: {
      byId: {},
    },
  } as MoodLightStore,
  reducers: {
    upsertAccountMoodLights(
      state: MoodLightStore,
      action: PayloadAction<MoodLight[]>,
    ) {
      action.payload.forEach(moodLight => {
        state.accountMoodLights.byId[moodLight.id] = moodLight;
      });
    },
    clearAccountMoodLights(
      state: MoodLightStore,
      action: PayloadAction<Record<string, any> | Except | Only>,
    ) {
      const options = action.payload;

      if (isExcept(options)) {
        state.accountMoodLights = {
          byId: Object.fromEntries(
            Object.entries(state.accountMoodLights.byId).filter(([key]) =>
              options.except.includes(key),
            ),
          ),
        };

        return;
      }

      if (isOnly(options)) {
        state.accountMoodLights = {
          byId: Object.fromEntries(
            Object.entries(state.accountMoodLights.byId).filter(
              ([key]) => !options.only.includes(key),
            ),
          ),
        };

        return;
      }

      state.accountMoodLights = {
        byId: {},
      };
    },
    upsertExclusiveMoodLights(
      state: MoodLightStore,
      action: PayloadAction<MoodLight[]>,
    ) {
      const byId = Object.fromEntries(action.payload.map(m => [m.id, m]));

      state.exclusiveMoodLights.byId = {
        ...state.exclusiveMoodLights.byId,
        ...byId,
      };
    },
    deleteMoodLight(state: MoodLightStore, action: PayloadAction<string>) {
      delete state.accountMoodLights.byId[action.payload];
    },
  },
});

export const accountMoodLightsSelector = createSelector(
  (state: Store) => state.moodLight.accountMoodLights.byId,
  byId => Object.values(byId),
);

export const exclusiveMoodLightsSelector = createSelector(
  (state: Store) => state.moodLight.exclusiveMoodLights.byId,
  byId => Object.values(byId),
);

export const currentDeviceExclusiveMoodLightsSelector = createSelector(
  currentDeviceModelNumberSelector,
  exclusiveMoodLightsSelector,
  (currentDeviceModelNumber, byId) =>
    byId.filter(m => {
      if (!m.productCodes?.length) return true;

      return m.productCodes.includes(currentDeviceModelNumber ?? '');
    }),
);

export const allMoodLightsSelector = createSelector(
  (state: Store) => state.moodLight,
  ({accountMoodLights, exclusiveMoodLights}) => ({
    ...accountMoodLights.byId,
    ...exclusiveMoodLights.byId,
  }),
);

export const currentDeviceMoodLightsSelector = createSelector(
  currentDeviceHeatProfilesSelector,
  allMoodLightsSelector,
  (currentDeviceHeatProfiles, allMoodLights) => {
    return currentDeviceHeatProfiles
      .map(p => getHeatProfileMoodLight(allMoodLights)(p))
      .filter(isDefined);
  },
);

export const allAndCurrentDeviceMoodLightsSelector = createSelector(
  allMoodLightsSelector,
  currentDeviceMoodLightsSelector,
  (allMoodLights, currentDeviceMoodLights) => {
    return {
      ...Object.fromEntries(
        currentDeviceMoodLights.map(item => [item.id, item]),
      ),
      ...allMoodLights,
    };
  },
);

export const {
  upsertAccountMoodLights,
  upsertExclusiveMoodLights,
  deleteMoodLight,
  clearAccountMoodLights,
} = moodLightSlice.actions;

export const moodLightReducer = moodLightSlice.reducer;
